import classNames from 'classnames';
import type { JSX, ReactNode } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { MarketTheme, MarketLink } from '@squareup/market-react';
import { withI18n } from '@squareup/service-i18n/components';
import { ErrorBoundary } from '@squareup/shared-ui-error-boundary';
import { useResponsiveBreakpoint } from '@squareup/ui-responsive-window';
import errorTrackingClient from '../../../utils/error-tracking-client';
import type { MoneyUnion } from '../../../utils/format-currency';
import type { LocationRowState } from '../location-selector/LocationRow/LocationRow';
import { FolderProgressBar } from './folder-progress-bar/FolderProgressBar';
import styles from './ProductPageHeader.module.scss';
import {
  Balance,
  Banners,
  ButtonDropdown,
  Buttons,
  Error,
  GoBack,
  Loading,
  ProductPageLocationSelector,
  Settings,
  AccountButton,
} from './ProductPageHeaderComponents';

export interface ProductPageHeaderProps {
  readonly secondaryClassName?: string;
  readonly title: string;
  readonly shouldCenterTitle?: boolean;
  readonly locationSelector?: JSX.Element;
  readonly currentUnitToken?: string;
  readonly units?: Subunit[];
  readonly handleSelectLocation?: (subunit: Subunit) => void;
  readonly goBack?: () => void;
  readonly subtitle?: string;
  readonly goToSettings?: () => void;
  readonly settingsMenuRows?: SettingsMenuRow[];
  readonly settingsIconType?: 'settings' | 'help';
  readonly settingsTooltipText?: ReactNode;
  readonly headline?: THeadline;
  readonly balance?: MoneyUnion;
  readonly balanceSuperText?: string;
  readonly balanceSubText?: TBalanceSubText;
  readonly progressData?: ProgressData;
  readonly buttons?: HeaderButton[];
  readonly buttonDropdown?: HeaderDropdown;
  readonly account?: AccountProps;
  readonly banners?: BannerProps[];
  readonly isLoading?: boolean;
  readonly hasError?: boolean;
  readonly onTryAgain?: () => void;
}

export interface SettingsMenuRow {
  onClick?: () => void;
  title: string;
  icon?: string;
  className?: string;
}

export interface Subunit {
  readonly nickname: string;
  readonly token: string;
  readonly rowState?: LocationRowState;
}

interface ProgressData {
  target: MoneyUnion;
  availableBalance: MoneyUnion;
}

export interface BalanceSubText {
  text: string | ReactNode;
  tooltipContent?: string;
  iconName?: string;
  fontWeight?: 'bold';
}

export type TBalanceSubText = BalanceSubText[] | ReactNode | string;

export interface Headline {
  text: string;
  subText?: string;
  subLink?: (
    | { text: string; href: string; target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target']; rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>['rel'] }
    | { text: string; onClick: () => void }
  );
}

export type THeadline = Headline | string;
export interface HeaderButton {
  readonly copy: string;
  readonly onClick: () => void;
  readonly icon?: string;
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
}

export interface HeaderDropdown {
  readonly copy: string;
  readonly contentList: ListProps | JSX.Element;
  readonly isDisabled?: boolean;
  readonly onClick?: () => void;
}

export interface ListProps {
  readonly className?: string;
  readonly slot?: string;
  readonly options: ListOption[];
}

interface ListOption {
  readonly key?: string;
  readonly icon?: string;
  readonly label: string;
  readonly onClick?: () => void;
}

interface BannerAction {
  readonly elementType: 'button' | 'link';
  readonly href?: string;
  readonly onClick?: () => void;
  readonly text: string;
}

export interface BannerProps {
  readonly key: string;
  readonly variant: 'info' | 'success' | 'warning' | 'critical';
  readonly className?: string;
  readonly title?: string;
  readonly description?: string | ReactNode;
  readonly descriptionHtml?: string;
  readonly actions?: BannerAction[];
  readonly dismissible?: boolean;
  readonly onDismiss?: () => void;
}

export interface AccountProps {
  readonly accountSuffix?: string;
  readonly accountNumber?: string;
  readonly routingNumber?: string;
}

interface MainContentProps extends ProductPageHeaderProps {
  readonly isSecondaryCentered?: boolean;
}

const MainContent = (props: MainContentProps) => {
  const {
    title,
    shouldCenterTitle,
    currentUnitToken,
    units,
    handleSelectLocation,
    goBack,
    locationSelector,
    subtitle,
    account,
    goToSettings,
    settingsMenuRows,
    settingsIconType,
    settingsTooltipText,
    headline,
    balance,
    balanceSuperText,
    balanceSubText,
    progressData,
    buttons,
    buttonDropdown,
    banners,
    isLoading,
    hasError,
    onTryAgain,
    isSecondaryCentered,
  } = props;
  const { isMobile, isDesktop } = useResponsiveBreakpoint();
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.titleCell}>
        <div className={shouldCenterTitle ? styles.titleCentered : ''}>
          <div className={styles.title}>{title}</div>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          <ProductPageLocationSelector
            currentUnitToken={currentUnitToken}
            units={units}
            handleSelectLocation={handleSelectLocation}
            locationSelector={locationSelector}
            shouldCenterTitle={shouldCenterTitle}
          />
        </div>
      </div>

      {hasError ? <Error t={t} onTryAgain={onTryAgain} /> : isLoading ? <Loading /> : null}

      {!isLoading && !hasError && (
        <>
          {account && <AccountButton t={t} account={account} isMobile={isMobile} />}
          {!isSecondaryCentered && <Settings
            t={t}
            goToSettings={goToSettings}
            settingsMenuRows={settingsMenuRows}
            iconType={settingsIconType}
            tooltipText={settingsTooltipText}
          />}
          {headline && (
            <>
              <div aria-hidden className={styles.spacerTop} />
              <div className={`${styles.headline} ${isSecondaryCentered ? styles.headlineSecondaryCentered : ''}`}>
                <div className="display-10">
                  {/* Using Trans here allows us to not escape tags like <br /> */}
                  <Trans
                    defaults={
                      isHeadlineObject(headline)
                        ? headline.text
                        : headline
                    }
                  />
                </div>
                {isHeadlineObject(headline) && headline.subText && (
                  <div className={`paragraph-30 ${styles.headlineSubText}`}>
                    {headline.subText}
                  </div>
                )}
                {isHeadlineObject(headline) && headline.subLink && (
                  <div className={`${styles.headlineSubLink}`}>
                    {'href' in headline.subLink
                      ? (
                        <MarketLink
                          href={headline.subLink.href}
                          target={headline.subLink.target}
                          rel={headline.subLink.rel}
                          data-testid="page-header-headline-sublink-href"
                        >
                          {headline.subLink.text}
                        </MarketLink>
                      )
                      : (
                        <MarketLink onClick={headline.subLink.onClick}>
                          {headline.subLink.text}
                        </MarketLink>
                      )}
                  </div>
                )}
              </div>
            </>
          )}
          {balance && (
            <Balance
              balance={balance}
              balanceSuperText={balanceSuperText}
              balanceSubText={balanceSubText}
              shouldAddBottomSpacer={(!isDesktop && !!buttons)}
            />
          )}
          {progressData && (
            <div className={styles.progressBar}>
              <FolderProgressBar target={progressData.target} balance={progressData.availableBalance} />
            </div>
          )}
          {buttons && <Buttons buttons={buttons} isMobile={isMobile} shouldWrap={!!goBack || buttons.length > 1} />}
          {buttonDropdown && (<ButtonDropdown buttonDropdown={buttonDropdown} />)}
          {banners && banners.length > 0 && <Banners banners={banners} />}
        </>
      )}
    </>
  );
};

export const ProductPageHeader = (props: ProductPageHeaderProps) => {
  const {
    secondaryClassName = '',
    onTryAgain,
    goBack,
    goToSettings,
    settingsMenuRows,
    settingsIconType,
    settingsTooltipText,
  } = props;
  const { isMobile } = useResponsiveBreakpoint();
  const { t } = useTranslation();

  /* Styling differs between mobile and desktop:
   * Larger screens: variant styles (headerPrimary/secondaryClassName) applied to inner grid wrapper
   * Mobile: variant styles applied directly to header (no wrapper)
   */
  const variantClass = secondaryClassName || styles.headerPrimary;
  const isSecondarySpread = secondaryClassName === styles.headerSecondarySpread;
  const isSecondaryCentered = secondaryClassName === styles.headerSecondaryCentered;
  const headerClass = classNames(styles.header, isMobile && variantClass);
  const mainContentClass = classNames(styles.mainContent, isSecondarySpread && styles.headerSecondarySpreadMainContent);

  return (
    <MarketTheme>
      <ErrorBoundary
        errorTrackingClient={errorTrackingClient}
        fallback={<Error t={t} onTryAgain={onTryAgain} />}
      >
        <header className={headerClass}>
          {goBack && <GoBack t={t} goBack={goBack} />}
          {!isMobile
            ? <div className={mainContentClass}>
              <div className={variantClass}>
                <MainContent isSecondaryCentered={isSecondaryCentered} {...props} />
              </div>
            </div>
            : <MainContent isSecondaryCentered={isSecondaryCentered} {...props} />}
          {isSecondaryCentered &&
            <Settings
              t={t}
              goToSettings={goToSettings}
              settingsMenuRows={settingsMenuRows}
              iconType={settingsIconType}
              tooltipText={settingsTooltipText}
            />
          }
        </header>
      </ErrorBoundary>
    </MarketTheme>
  );
};

function isHeadlineObject(v: THeadline): v is Headline {
  return typeof v === 'object' && 'text' in v;
}

export const ProductPageHeaderI18n = withI18n(ProductPageHeader);
